import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GD } from "src/GD";
import { loadClientDirectory } from "src/services/clientDirectory";
import { createMerchantNavigation } from "./merchantNavigation";
import type { MerchantDestination, MerchantNeighbors } from "./merchantNavigation";

interface PopupTarget {
	merchantID: number;
	clienttID?: number;
	client_user_id?: number;
	channelId?: number;
}

const requestError = (error: unknown): IError => {
	if (error && typeof error === "object" && "code" in error && typeof error.code === "number" && "message" in error && typeof error.message === "string") {
		return error as IError;
	}
	return {
		code: -1,
		message: error instanceof Error ? error.message : msg("couldNotLoadMerchant"),
	};
};

const loadMerchant = async (target: PopupTarget) => {
	const response = await GD.REQ_API.request({
		method: "merchants/group/get",
		data: { group_id: target.merchantID, client_id: target.clienttID },
	});
	if (response.error) throw response.error;
	if (!response.data) throw new Error(msg("noMerchantReturnedFromServer"));
	const merchant = response.data as IClientGroup;
	if (merchant.id !== target.merchantID || (target.clienttID && merchant.client_id !== target.clienttID)) {
		throw new Error(msg("theServerReturnedADifferentMerchant"));
	}
	return merchant;
};

export const useMerchantPopupNavigation = (request: PopupTarget | null | undefined, projectId: number | undefined) => {
	const localeRevision = useLocale();
	const [merchant, setMerchant] = useState<IClientGroup | null>(null);
	const [activeTarget, setActiveTarget] = useState<PopupTarget | null>(null);
	const [error, setError] = useState<IError | null>(null);
	const [loading, setLoading] = useState(false);
	const [switching, setSwitching] = useState(false);
	const [direction, setDirection] = useState<-1 | 0 | 1>(0);
	const [neighbors, setNeighbors] = useState<MerchantNeighbors | null>(null);
	const [neighborsLoading, setNeighborsLoading] = useState(false);
	const [neighborsError, setNeighborsError] = useState(false);
	const [retry, setRetry] = useState(0);
	const version = useRef(0);
	const targetRef = useRef<PopupTarget | null>(null);
	const switchingRef = useRef(false);

	const navigation = useMemo(() => createMerchantNavigation(
		async () => {
			return loadClientDirectory({}, { expectedProjectId: projectId });
		},
		async clientId => {
			const response = await GD.REQ_API.request({ method: "merchants/list", data: { client_id: clientId } });
			if (response.error || !Array.isArray(response.data)) throw new Error(msg("couldNotLoadMerchants"));
			return response.data;
		},
	// The cache belongs to one popup session and one project, even if its loaders are unchanged.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	), [localeRevision, request, projectId]);

	useEffect(() => {
		const currentVersion = ++version.current;
		targetRef.current = request ?? null;
		switchingRef.current = false;
		setMerchant(null);
		setActiveTarget(request ?? null);
		setError(null);
		setDirection(0);
		setSwitching(false);
		setNeighbors(null);
		setNeighborsError(false);
		setLoading(!!request);
		if (request) {
			loadMerchant(request).then(result => {
				if (version.current === currentVersion) setMerchant(result);
			}).catch(cause => {
				if (version.current === currentVersion) setError(requestError(cause));
			}).finally(() => {
				if (version.current === currentVersion) setLoading(false);
			});
		}
		// Invalidate every load started during this session, including a later navigation.
		const invalidateSession = () => { version.current++; };
		return invalidateSession;
	}, [request, projectId]);

	useEffect(() => {
		let cancelled = false;
		setNeighbors(null);
		setNeighborsError(false);
		setNeighborsLoading(!!merchant);
		if (merchant) {
			navigation.neighbors(merchant).then(result => {
				if (!cancelled) setNeighbors(result);
			}).catch(() => {
				if (!cancelled) setNeighborsError(true);
			}).finally(() => {
				if (!cancelled) setNeighborsLoading(false);
			});
		}
		return () => { cancelled = true; };
	}, [merchant, navigation, retry]);

	const refresh = useCallback(async () => {
		if (!targetRef.current || switchingRef.current) return;
		const currentVersion = ++version.current;
		setError(null);
		try {
			const result = await loadMerchant(targetRef.current);
			if (currentVersion === version.current) {
				setMerchant(result);
				setLoading(false);
			}
		} catch (cause) {
			if (currentVersion === version.current) {
				setError(requestError(cause));
				setLoading(false);
			}
		}
	}, []);

	const navigate = async (destination: MerchantDestination, nextDirection: -1 | 1) => {
		if (switchingRef.current || !targetRef.current) return false;
		switchingRef.current = true;
		const currentVersion = ++version.current;
		setSwitching(true);
		setDirection(nextDirection);
		setError(null);
		const target: PopupTarget = {
			merchantID: destination.merchant.id,
			clienttID: destination.client.id,
			client_user_id: destination.client.user_id,
		};
		try {
			const result = await loadMerchant(target);
			if (version.current !== currentVersion) return false;
			targetRef.current = target;
			setActiveTarget(target);
			setMerchant(result);
			return true;
		} catch (cause) {
			if (version.current === currentVersion) setError(requestError(cause));
			return false;
		} finally {
			if (version.current === currentVersion) {
				switchingRef.current = false;
				setSwitching(false);
			}
		}
	};

	return { merchant, activeTarget, error, loading, switching, direction, neighbors, neighborsLoading, neighborsError, refresh, navigate, retryNeighbors: () => setRetry(value => value + 1) };
};
